import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AiProviderTile,
  AiTrademarkInfo,
  DrawerBody,
  DrawerGroup,
  DrawerSection,
  InfoButton,
  Sheet,
  Surface,
  useDrawerSections,
  useUnsavedChanges,
} from "@vd/ui";
import { Building2, ChevronRight, Search, Send } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { z } from "zod";
import { providerQuery, providersQuery, qk, serverSettingsQuery } from "../../../api/admin";
import { errorMessage } from "../../../api/client";
import {
  type ConnectorProvider,
  connectionsOf,
  connectorQuery,
  connectorSummary,
  PROVIDER_META,
  providerOf,
} from "../../../api/connector";
import { CONSENT_TOAST, type EmailAdmin, emailAdminQuery, emailKeys } from "../../../api/email";
import {
  type IntegrationKind,
  type IntegrationView,
  integrationsApi,
  integrationsKey,
  integrationsQuery,
} from "../../../api/integrations";
import { addressesQuery, companyHasEmailMarketing, domainsQuery } from "../../../api/sending";
import type { ProviderConfig, ProviderName, ProviderTestResult } from "../../../api/types";
import { MicrosoftMark, OdooMark, OpenMetalMark, TelnyxMark, XaiMark } from "../../../assets/logos";
import { requireAdmin, useMe } from "../../../auth/useMe";
import { Crumbs } from "../../../components/Crumbs";
import { ConnectorPanel } from "../../../components/integrations/ConnectorPanel";
import { MicrosoftSheet } from "../../../components/integrations/EmailSettings";
import { OdooDrawer } from "../../../components/integrations/OdooSettings";
import {
  ChangeMeta,
  DiscardDialog,
  keysStatus,
  LastTest,
  ProviderDanger,
  ProviderKeys,
  type ProviderSettingsDraft,
  ProviderSettingsFields,
  StatusPill,
  settingsStatus,
  useProviderSettings,
  useProviderTest,
} from "../../../components/integrations/ProviderParts";
import { RequestIntegration } from "../../../components/integrations/RequestIntegration";
import { SlowWait } from "../../../components/SlowWait";
import { toast } from "../../../components/toast";
import { Button, Card, cx, Dialog, ErrorState, Input, PageHeader, Spinner } from "../../../components/ui";
import {
  arrangeIntegrations,
  type CatalogEntry,
  type IntegrationCategory,
  matchesQuery,
} from "../../../lib/integrationCatalog";
import { useNavigate } from "../../../lib/navigate";
import {
  odooStatus,
  PROVIDER_FIELDS,
  PROVIDER_ORDER,
  type ProviderStatus,
  providerStatus,
  providerSummary,
  STATUS_LABEL,
  USED_BY,
} from "../../../lib/providers";
import { sendingCard } from "../../../lib/sending";

const searchSchema = z.object({
  /** Where the server sends the browser back after Microsoft's approval page. */
  email_consent: z
    .enum(["approved", "declined", "wrong_tenant", "no_entra_tenant", "not_admin", "expired"])
    .optional()
    .catch(undefined),
  /** A card to open, from `/admin/integrations/{kind}` ("microsoft-365", "telnyx", "odoo"). */
  open: z.string().optional().catch(undefined),
});

/** The drawer a `/admin/integrations/{kind}` address opens, or null for an unknown kind or a
 * provider the company does not own (`owned`: the kinds in the provider list). */
export function openIdOf(kind: string, owned: readonly string[]): OpenId | null {
  if (kind === "microsoft-365" || kind === "microsoft") return "microsoft";
  if (kind === "claude" || kind === "chatgpt") return `connector_${kind}`;
  if (kind === "odoo") return "crm:odoo";
  if ((PROVIDER_ORDER as readonly string[]).includes(kind) && kind !== "email" && owned.includes(kind))
    return kind as SheetProvider;
  return null;
}

/** A provider kind's address, which waits for the list before it opens or lands with no drawer. */
const isProviderKind = (kind: string) => (PROVIDER_ORDER as readonly string[]).includes(kind);

export const Route = createFileRoute("/admin/integrations/")({
  staticData: { crumb: { label: "Integrations", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      // Only the list: it holds the granted kinds (ADR 0050). A kind's own read waits for its
      // drawer, so a company never asks for a provider it does not own.
      context.queryClient.prefetchQuery(providersQuery),
      context.queryClient.prefetchQuery(integrationsQuery),
      context.queryClient.prefetchQuery(connectorQuery),
      context.queryClient.prefetchQuery(emailAdminQuery),
    ]),
  component: IntegrationsPage,
});

// Brand presentation. Brand colors appear only in the logo tile and the card's top edge.

/** The connector's two cards, one per provider reps connect (DESIGN.md, "Claude and ChatGPT for
 * reps"). */
type ConnectorCardName = "connector_claude" | "connector_chatgpt";

/** A provider with a card and drawer of its own. The `email` provider (Kite's sender) lives in
 * the Microsoft 365 card with the reps' mailboxes (ADR 0024). */
type SheetProvider = Exclude<ProviderName, "email">;

type CardName = SheetProvider | "microsoft" | "odoo" | ConnectorCardName;

const CONNECTOR_CARD: Record<ConnectorCardName, ConnectorProvider> = {
  connector_claude: "claude",
  connector_chatgpt: "chatgpt",
};

const isConnectorCard = (n: string): n is ConnectorCardName => n in CONNECTOR_CARD;

interface Brand {
  title: string;
  tile: string;
  mark: ReactNode;
  /** Thin gradient along the card's top edge. */
  edge: string;
}

const NEUTRAL_TILE = "bg-surface-2 shadow-[inset_0_0_0_1px_var(--vd-hairline)]";

const BRAND: Record<CardName, Brand> = {
  telnyx: {
    title: "Telnyx",
    tile: "bg-[#00e3aa] text-black",
    mark: <TelnyxMark className="size-[30px]" />,
    edge: "linear-gradient(90deg, #00e3aa 0%, #00e3aa 35%, transparent 90%)",
  },
  xai: {
    title: "xAI",
    tile: "bg-contrast text-on-contrast",
    mark: <XaiMark className="w-[38px]" />,
    edge: "linear-gradient(90deg, var(--vd-ink) 0%, var(--vd-ink-3) 40%, transparent 85%)",
  },
  xai_research: {
    title: "xAI for lead generation",
    tile: "bg-contrast text-on-contrast",
    mark: <XaiMark className="w-[38px]" />,
    edge: "linear-gradient(90deg, var(--vd-ink-3) 0%, transparent 70%)",
  },
  brave: {
    title: "Brave Search",
    tile: "bg-[#fb542b] text-white",
    mark: <Search className="size-7" strokeWidth={2.25} />,
    edge: "linear-gradient(90deg, #fb542b 0%, #fb542b 25%, transparent 80%)",
  },
  storage: {
    title: "Storage",
    tile: NEUTRAL_TILE,
    mark: <OpenMetalMark className="size-8" />,
    edge: "linear-gradient(90deg, #267ab5 0%, #267ab5 25%, #c4ccd4 55%, transparent 90%)",
  },
  microsoft: {
    title: "Microsoft 365",
    tile: NEUTRAL_TILE,
    mark: <MicrosoftMark className="size-[26px]" />,
    edge: "linear-gradient(90deg, var(--vd-hairline-strong) 0%, transparent 70%)",
  },
  // The tile is AiProviderTile with Anthropic's own symbol (see LogoTile); tile and mark are unused.
  anthropic: {
    title: "Anthropic",
    tile: NEUTRAL_TILE,
    mark: null,
    edge: "linear-gradient(90deg, #d97757 0%, #d97757 25%, transparent 80%)",
  },
  // The tile is AiProviderTile (the vendor's own mark, see LogoTile); tile and mark are unused.
  connector_claude: {
    title: "Claude",
    tile: NEUTRAL_TILE,
    mark: null,
    edge: "linear-gradient(90deg, var(--vd-hairline-strong) 0%, transparent 70%)",
  },
  // Codex signs in with a ChatGPT account and counts under the ChatGPT switch (story EM23).
  connector_chatgpt: {
    title: "ChatGPT and Codex",
    tile: NEUTRAL_TILE,
    mark: null,
    edge: "linear-gradient(90deg, var(--vd-hairline-strong) 0%, transparent 70%)",
  },
  odoo: {
    title: "Odoo",
    tile: NEUTRAL_TILE,
    mark: <OdooMark className="w-[38px]" />,
    edge: "linear-gradient(90deg, #714b67 0%, #714b67 25%, transparent 80%)",
  },
};

/** The logo tile: 48px on cards, 40px in drawer headers (DESIGN.md, "Integration card"). Marks
 * are drawn for the 48px tile and scaled as vectors for smaller ones, so they stay sharp. */
function LogoTile({ name, size = 48 }: { name: CardName; size?: number }) {
  if (isConnectorCard(name)) return <AiProviderTile provider={CONNECTOR_CARD[name]} size={size} />;
  if (name === "anthropic") return <AiProviderTile provider="anthropic" size={size} />;
  const b = BRAND[name];
  return (
    <span
      aria-hidden
      className={cx("inline-flex shrink-0 items-center justify-center", b.tile)}
      style={{ width: size, height: size, borderRadius: Math.round(size / 4) }}
    >
      <span className="inline-flex" style={size === 48 ? undefined : { transform: `scale(${size / 48})` }}>
        {b.mark}
      </span>
    </span>
  );
}

/** Beside a provider drawer's title: what uses it, and Anthropic's trademark note. */
function TitleAside({ name }: { name: SheetProvider }) {
  return (
    <span className="flex items-center gap-0.5">
      {name === "anthropic" ? <AiTrademarkInfo provider="anthropic" /> : null}
      <InfoButton kind="help" label={`About ${BRAND[name].title}`}>
        {USED_BY[name]}
      </InfoButton>
    </span>
  );
}

// Microsoft 365: Kite's sender and the reps' mailboxes, summarized together.

interface MicrosoftState {
  status: ProviderStatus;
  label: string;
  summary: string | null;
  /** Why it needs attention: the sender's last failure. */
  reason: string | null;
  /** Set up at all: the sender has a key, or the mailboxes are on. */
  installed: boolean;
}

/** The pill: the sender's health first; otherwise Connected (sender working), On (mailboxes on
 * without a working sender) or Not set up. The line: the sender, then the reps connected. A
 * company without the Microsoft sender (null, ADR 0050) counts only its mailboxes. */
export function microsoftState(sender: ProviderConfig | null, email: EmailAdmin): MicrosoftState {
  const on = email.settings.microsoft_enabled;
  const reps = on ? `${email.accounts.connected} of ${email.accounts.members} reps` : null;
  if (!sender) {
    return on
      ? { status: "connected", label: "On", summary: reps, reason: null, installed: true }
      : { status: "not_set_up", label: "Off", summary: null, reason: null, installed: false };
  }
  const s = providerStatus(sender);
  const parts = [sender.settings.sender?.trim() || null, reps].filter(Boolean);
  const summary = parts.length ? parts.join(" · ") : null;
  const installed = s !== "not_set_up" || on;
  if (s === "attention")
    return {
      status: "attention",
      label: STATUS_LABEL.attention,
      summary,
      reason: sender.health.message ?? "Not tested yet",
      installed,
    };
  if (s === "connected")
    return { status: "connected", label: STATUS_LABEL.connected, summary, reason: null, installed };
  if (on) return { status: "connected", label: "On", summary, reason: null, installed };
  return { status: "not_set_up", label: STATUS_LABEL.not_set_up, summary, reason: null, installed };
}

// The catalog: what the company connects (Installed and Add more) and its own accounts.

/** The providers with a card in Your accounts, in order (Kite's sender lives in Microsoft 365). */
const SERVICE_ORDER = [
  "telnyx",
  "xai",
  "xai_research",
  "brave",
  "storage",
  "anthropic",
] as const satisfies readonly SheetProvider[];

type ServiceName = (typeof SERVICE_ORDER)[number];

const SERVICE_CATALOG: Record<ServiceName, { category: IntegrationCategory; keywords?: string[] }> = {
  telnyx: { category: "Calling", keywords: ["Phone", "SMS", "Numbers"] },
  xai: { category: "AI", keywords: ["Grok"] },
  xai_research: { category: "AI", keywords: ["Grok", "Lead generation", "Research"] },
  brave: { category: "Search" },
  storage: { category: "Storage", keywords: ["OpenMetal", "S3", "Recordings"] },
  anthropic: { category: "AI" },
};

/** The providers the company owns (ADR 0050), from the one list read. `owned` is null while the
 * list loads or when it failed: then no provider card, drawer or sender shows. */
function useOwnedProviders(): { owned: ProviderConfig[] | null; settled: boolean } {
  const q = useQuery(providersQuery);
  return { owned: q.data ?? null, settled: !q.isPending };
}

const ownedKinds = (owned: ProviderConfig[] | null): string[] =>
  (owned ?? []).flatMap((p) => (p.kind ? [p.kind] : []));

/** The Email sending card opens its own page, not a drawer. */
const EMAIL_SENDING = "email_sending";

/** What opens: a provider's drawer, Microsoft 365, Claude or ChatGPT, a CRM by its kind, or the
 * Email sending page. */
type OpenId = SheetProvider | "microsoft" | ConnectorCardName | `crm:${string}` | typeof EMAIL_SENDING;

/** One card in Installed or Add more. */
interface Entry extends CatalogEntry {
  id: OpenId;
  /** The CRM's server kind ("odoo"); CRMs only. */
  crm?: string;
  tile: ReactNode;
  edge: string;
  status: ProviderStatus | null;
  label?: string | undefined;
  summary: string | null;
  reason: string | null;
  error: boolean;
  info?: ReactNode;
}

const GENERIC_EDGE = "linear-gradient(90deg, var(--vd-hairline-strong) 0%, transparent 70%)";

function GenericTile({ size = 48 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className={cx("inline-flex shrink-0 items-center justify-center text-ink", NEUTRAL_TILE)}
      style={{ width: size, height: size, borderRadius: Math.round(size / 4) }}
    >
      <Building2 style={{ width: size / 2, height: size / 2 }} strokeWidth={1.75} />
    </span>
  );
}

function SendingTile({ size = 48 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className={cx("inline-flex shrink-0 items-center justify-center text-ink", NEUTRAL_TILE)}
      style={{ width: size, height: size, borderRadius: Math.round(size / 4) }}
    >
      <Send style={{ width: size / 2.4, height: size / 2.4 }} strokeWidth={1.75} />
    </span>
  );
}

function crmTile(kind: string, size = 48): ReactNode {
  return kind === "odoo" ? <LogoTile name="odoo" size={size} /> : <GenericTile size={size} />;
}

/** The CRMs the server knows (every tenant integration is a CRM), Microsoft 365, Claude and
 * ChatGPT, each with whether the company has it set up. */
function useEntries(owned: ProviderConfig[] | null, settled: boolean): Entry[] {
  const crms = useQuery(integrationsQuery);
  const connector = useQuery(connectorQuery);
  const email = useQuery(emailAdminQuery);
  const me = useMe();
  const marketing = companyHasEmailMarketing(me);
  const domains = useQuery({ ...domainsQuery, enabled: marketing });
  const addresses = useQuery({ ...addressesQuery, enabled: marketing });
  // Kite's sender counts only when the company owns it; a failed list counts only mailboxes.
  const sender = owned?.find((p) => p.kind === "email") ?? null;
  const out: Entry[] = [];

  const views: Pick<IntegrationView, "kind" | "name">[] = crms.data ?? [{ kind: "odoo", name: "Odoo" }];
  for (const base of views) {
    const v = crms.data?.find((x) => x.kind === base.kind);
    const status = v ? odooStatus(v) : crms.isError ? "attention" : null;
    out.push({
      id: `crm:${base.kind}`,
      crm: base.kind,
      title: base.kind === "odoo" ? BRAND.odoo.title : base.name,
      category: "CRM",
      installed: v ? v.configured : true,
      tile: crmTile(base.kind),
      edge: base.kind === "odoo" ? BRAND.odoo.edge : GENERIC_EDGE,
      status,
      summary: v?.url ? `${v.url.replace(/^https?:\/\//, "")} · ${v.database ?? ""}` : null,
      reason: v && status === "attention" ? (v.health.message ?? "Not tested yet") : null,
      error: crms.isError,
    });
  }

  const ms = settled && email.data ? microsoftState(sender, email.data) : null;
  const msError = email.isError;
  out.push({
    id: "microsoft",
    title: BRAND.microsoft.title,
    category: "Email",
    keywords: ["Outlook", "Mailbox"],
    installed: ms ? ms.installed : true,
    tile: <LogoTile name="microsoft" />,
    edge: BRAND.microsoft.edge,
    status: ms ? ms.status : msError ? "attention" : null,
    label: ms?.label,
    summary: ms?.summary ?? null,
    reason: ms?.reason ?? null,
    error: msError,
  });

  // Email sending: the company's domains and addresses (story EM29), behind email marketing.
  if (marketing) {
    const card =
      domains.data && addresses.data ? sendingCard(domains.data.items, addresses.data.items.length) : null;
    const failed = domains.isError || addresses.isError;
    out.push({
      id: EMAIL_SENDING,
      title: "Email sending",
      category: "Email",
      keywords: ["Email sending", "Marketing", "Domain", "DNS"],
      installed: card ? card.installed : true,
      tile: <SendingTile />,
      edge: GENERIC_EDGE,
      status: card ? card.status : failed ? "attention" : null,
      label: card?.label,
      summary: card?.summary ?? null,
      reason: null,
      error: failed,
    });
  }

  for (const name of ["connector_claude", "connector_chatgpt"] as const) {
    const provider = CONNECTOR_CARD[name];
    const ps = connector.data ? providerOf(connector.data, provider) : null;
    out.push({
      id: name,
      title: BRAND[name].title,
      category: "AI",
      keywords: [PROVIDER_META[provider].vendor],
      // Turned on at least once: an Off switch stays in Installed.
      installed: ps ? ps.enabled || ps.updated_at !== null : true,
      tile: <LogoTile name={name} />,
      edge: BRAND[name].edge,
      status: ps ? (ps.enabled ? "connected" : "not_set_up") : connector.isError ? "attention" : null,
      label: ps ? (ps.enabled ? "On" : "Off") : undefined,
      summary:
        connector.data && ps
          ? connectorSummary({ enabled: ps.enabled, connections: connectionsOf(connector.data, provider) })
          : null,
      reason: null,
      error: connector.isError,
      info: <AiTrademarkInfo provider={provider} />,
    });
  }
  return out;
}

// Page

function IntegrationsPage() {
  const [open, setOpen] = useState<OpenId | null>(null);
  const [fromConsent, setFromConsent] = useState(false);
  const [query, setQuery] = useState("");
  const [switching, setSwitching] = useState<Entry | null>(null);
  const { email_consent: consent, open: openKind } = Route.useSearch();
  const me = useMe();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  // Back from Microsoft's approval page: say how it went, show Microsoft 365, drop the param.
  useEffect(() => {
    if (!consent) return;
    const t = CONSENT_TOAST[consent];
    toast(t.text, t.tone);
    qc.invalidateQueries({ queryKey: emailKeys.admin });
    setFromConsent(true);
    setOpen("microsoft");
    void navigate({ search: {}, replace: true });
  }, [consent, navigate, qc]);
  const { owned, settled } = useOwnedProviders();
  const kinds = ownedKinds(owned);
  const kindsKey = kinds.join(",");
  // `/admin/integrations/{kind}` opens that card's drawer. A provider's address waits for the
  // list, then opens only a provider the company owns; any other lands here with no drawer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `kindsKey` stands for `kinds`.
  useEffect(() => {
    if (!openKind || consent) return;
    if (isProviderKind(openKind) && !settled) return;
    const id = openIdOf(openKind, kinds);
    if (id) setOpen(id);
    void navigate({ search: {}, replace: true });
  }, [openKind, consent, navigate, settled, kindsKey]);
  const close = () => {
    setOpen(null);
    setFromConsent(false);
  };
  const openEntry = (id: OpenId) => {
    if (id === EMAIL_SENDING) void navigate({ to: "/admin/integrations/email-sending", search: {} });
    else setOpen(id);
  };

  const entries = useEntries(owned, settled);
  const { installed, addMore } = arrangeIntegrations(entries, query);
  const searching = query.trim() !== "";
  // One card per provider the server returned, in display order; none while loading or failed.
  const services = SERVICE_ORDER.flatMap((n) => {
    const config = owned?.find((p) => p.kind === n);
    return config && matchesQuery({ title: BRAND[n].title, ...SERVICE_CATALOG[n] }, query)
      ? [{ name: n, config }]
      : [];
  });
  const hasSender = kinds.includes("email");
  const nothing = searching && !installed.length && !addMore.length && !services.length;

  return (
    <>
      <PageHeader title="Integrations" crumbs={<Crumbs />} />
      <div className="mb-6 w-full max-w-[320px]">
        <Input
          type="search"
          aria-label="Search integrations"
          placeholder="Search"
          leading={<Search aria-hidden />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && query) {
              e.preventDefault();
              setQuery("");
            }
          }}
        />
      </div>
      {nothing ? (
        <p role="status" className="mb-6 text-body text-ink-3">
          No integrations match "{query.trim()}".
        </p>
      ) : null}
      {installed.length ? (
        <CardSection title="Installed">
          {installed.map((e) => (
            <li key={e.id} className="min-w-0">
              <InstalledCard entry={e} onOpen={() => openEntry(e.id)} />
            </li>
          ))}
        </CardSection>
      ) : null}
      {addMore.length ? (
        <CardSection title="Add more">
          {addMore.map(({ entry, switchCrm }) => (
            <li key={entry.id} className="min-w-0">
              <AddCard
                entry={entry}
                switchCrm={switchCrm}
                onOpen={() => (switchCrm ? setSwitching(entry) : openEntry(entry.id))}
              />
            </li>
          ))}
        </CardSection>
      ) : null}
      <RequestIntegration />
      {services.length ? (
        <CardSection title="Your accounts">
          {services.map(({ name, config }) => (
            <li key={name} className="min-w-0">
              <ProviderCard name={name} config={config} onOpen={() => setOpen(name)} />
            </li>
          ))}
        </CardSection>
      ) : null}
      {/* On the ops console once it is set up (story CA4); the server sets it only for a
          company that owns a provider (ADR 0050). */}
      {!searching && me.operator_pages ? <ServerSettingsCard /> : null}
      {open === "microsoft" ? (
        <MicrosoftDrawer
          onClose={close}
          attention={fromConsent ? "mailboxes" : null}
          sender={hasSender}
          senderConfig={owned?.find((p) => p.kind === "email") ?? null}
        />
      ) : open?.startsWith("crm:") ? (
        open === "crm:odoo" ? (
          <OdooSheet onClose={close} />
        ) : (
          <CrmSheet key={open} kind={open.slice(4)} onClose={close} />
        )
      ) : open && isConnectorCard(open) ? (
        <ConnectorSheet key={open} name={open} onClose={close} />
      ) : open && kinds.includes(open) ? (
        <ProviderSheet key={open} name={open as SheetProvider} onClose={close} />
      ) : null}
      <SwitchCrmDialog
        target={switching}
        current={entries.filter((e) => e.category === "CRM" && e.installed)}
        onClose={() => setSwitching(null)}
        onSwitched={(id) => {
          setSwitching(null);
          openEntry(id);
        }}
      />
    </>
  );
}

function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="mb-8 max-w-[1040px]">
      <h2 className="mb-3 font-medium text-ink text-title">{title}</h2>
      <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">{children}</ul>
    </section>
  );
}

/** Every Integrations card has this one shape and one fixed height (DESIGN.md, "Integration
 * card"): the logo tile, the name, the status pill, one short status line, and the whole card as
 * the one action that opens the drawer. Everything else lives in the drawer. */
function CardShell({
  title,
  tile,
  edge,
  label,
  pill,
  line,
  trailing,
  onOpen,
  info,
}: {
  title: string;
  tile: ReactNode;
  /** Thin gradient along the card's top edge. */
  edge: string;
  /** The button's accessible name: the title and the state ("Odoo, Connected", "Odoo, Add"). */
  label: string;
  /** The status pill, a spinner while loading, or nothing (Add more). */
  pill: ReactNode;
  /** The status line: a key's last four, a count, an address, a reason or a category. */
  line: ReactNode;
  /** A word before the chevron: "Add" or "Switch CRM". */
  trailing?: string;
  onOpen: () => void;
  /** A control beside the name (the trademark Info button). It sits over the card rather than
   * inside its button, so it never opens the drawer. */
  info?: ReactNode;
}) {
  return (
    <Surface radius="xl" className="relative flex h-32 flex-col overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5"
        style={{ background: edge } as CSSProperties}
      />
      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        className={cx(
          "group flex h-full w-full flex-col p-4 text-left transition-colors duration-150",
          "hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] focus-visible:outline-none",
        )}
      >
        <span className="flex h-12 w-full items-center gap-3">
          {tile}
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate font-medium text-ink text-title">{title}</span>
            {info ? <span aria-hidden className="w-5 shrink-0" /> : null}
          </span>
          {pill}
        </span>
        <span className="mt-auto flex w-full items-center gap-3 border-hairline border-t pt-2.5 text-caption">
          {line}
          {trailing ? <span className="shrink-0 font-medium text-ink-2">{trailing}</span> : null}
          <ChevronRight
            aria-hidden
            className="size-4 shrink-0 text-ink-3 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-ink"
          />
        </span>
      </button>
      {info ? (
        // Lays out like the name row inside the button: the name's width comes from the same text
        // in a pseudo-element (not in the DOM, so it is read only once), then the control.
        <span className="pointer-events-none absolute inset-x-0 top-0 flex h-20 items-center gap-3 px-4">
          <span aria-hidden className="size-12 shrink-0" />
          <span className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden
              data-title={title}
              className="invisible truncate font-medium text-title before:content-[attr(data-title)]"
            />
            <span className="pointer-events-auto inline-flex">{info}</span>
          </span>
        </span>
      ) : null}
    </Surface>
  );
}

/** The status line: an error, the reason it needs attention, or the real configuration. */
function StatusLine({
  error,
  reason,
  summary,
}: {
  error: boolean;
  reason?: string | null;
  summary: string | null;
}) {
  if (error)
    return <span className="min-w-0 flex-1 truncate text-danger-ink">Couldn't load. Open to retry.</span>;
  if (reason)
    return (
      <span className="min-w-0 flex-1 truncate text-warning" title={reason}>
        {reason}
      </span>
    );
  return <span className="min-w-0 flex-1 truncate font-mono text-ink-2">{summary ?? "Not set"}</span>;
}

function InstalledCard({ entry: e, onOpen }: { entry: Entry; onOpen: () => void }) {
  const label = e.status ? (e.label ?? STATUS_LABEL[e.status]) : null;
  return (
    <CardShell
      title={e.title}
      tile={e.tile}
      edge={e.edge}
      label={`${e.title}${label ? `, ${label}` : ""}`}
      pill={e.status ? <StatusPill status={e.status} label={e.label} /> : <Spinner />}
      line={<StatusLine error={e.error} reason={e.reason} summary={e.summary} />}
      onOpen={onOpen}
      info={e.info}
    />
  );
}

function AddCard({ entry: e, switchCrm, onOpen }: { entry: Entry; switchCrm: boolean; onOpen: () => void }) {
  const action = switchCrm ? "Switch CRM" : "Add";
  return (
    <CardShell
      title={e.title}
      tile={e.tile}
      edge={e.edge}
      label={`${e.title}, ${action}`}
      pill={null}
      line={<span className="min-w-0 flex-1 truncate text-ink-3">{e.category}</span>}
      trailing={action}
      onOpen={onOpen}
      info={e.info}
    />
  );
}

/** A provider the company owns, drawn from its row in the list (no read of its own). */
function ProviderCard({
  name,
  config,
  onOpen,
}: {
  name: SheetProvider;
  config: ProviderConfig;
  onOpen: () => void;
}) {
  const b = BRAND[name];
  const status = providerStatus(config);
  return (
    <CardShell
      title={b.title}
      tile={<LogoTile name={name} />}
      edge={b.edge}
      label={`${b.title}, ${STATUS_LABEL[status]}`}
      pill={<StatusPill status={status} />}
      line={<StatusLine error={false} summary={providerSummary(name, config)} />}
      onOpen={onOpen}
      info={name === "anthropic" ? <AiTrademarkInfo provider="anthropic" /> : undefined}
    />
  );
}

/** One CRM per company: switching removes the installed CRM's setup, then opens the new one's. */
function SwitchCrmDialog({
  target,
  current,
  onClose,
  onSwitched,
}: {
  target: Entry | null;
  current: Entry[];
  onClose: () => void;
  onSwitched: (id: OpenId) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const from = current.map((e) => e.title).join(" and ") || "current CRM";
  const to = target?.title ?? "";
  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      for (const e of current) {
        if (!e.crm) continue;
        const next = await integrationsApi.remove(e.crm as IntegrationKind);
        qc.setQueryData<IntegrationView[]>(integrationsKey, (list) =>
          (list ?? []).map((x) => (x.kind === next.kind ? next : x)),
        );
      }
      qc.invalidateQueries({ queryKey: qk.overview });
      onSwitched(target.id);
    } catch (e) {
      toast(errorMessage(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      tone="danger"
      size="sm"
      title={`Switch to ${to}?`}
      description={`Kite uses one CRM. This removes the ${from} setup and opens ${to}'s setup. Reps reconnect to ${to}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" busy={busy} onClick={() => void confirm()}>
            Switch CRM
          </Button>
        </>
      }
    />
  );
}

/** A CRM the console has no settings for yet: its header and status. */
function CrmSheet({ kind, onClose }: { kind: string; onClose: () => void }) {
  const q = useQuery(integrationsQuery);
  const v = q.data?.find((x) => x.kind === kind);
  return (
    <Sheet
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={v?.name ?? kind}
      media={crmTile(kind, 40)}
      status={v ? <StatusPill status={odooStatus(v)} /> : null}
      width="lg"
    >
      <DrawerBody>{null}</DrawerBody>
    </Sheet>
  );
}

function ConnectorSheet({ name, onClose }: { name: ConnectorCardName; onClose: () => void }) {
  const q = useQuery(connectorQuery);
  const provider = CONNECTOR_CARD[name];
  const ps = q.data ? providerOf(q.data, provider) : null;
  return (
    <Sheet
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={BRAND[name].title}
      titleAside={<AiTrademarkInfo provider={provider} />}
      media={<LogoTile name={name} size={40} />}
      status={
        ps ? (
          <StatusPill status={ps.enabled ? "connected" : "not_set_up"} label={ps.enabled ? "On" : "Off"} />
        ) : null
      }
      width="lg"
    >
      <ConnectorPanel provider={provider} />
    </Sheet>
  );
}

// Set on the server

function ServerSettingsCard() {
  const q = useQuery(serverSettingsQuery);
  return (
    <Card
      className="mt-6 max-w-[1040px]"
      title={
        <span className="flex items-center gap-1">
          Set on the server
          <InfoButton kind="help" label="About server settings">
            These live in the server's environment file. Change them over SSH.
          </InfoButton>
        </span>
      }
    >
      {q.isError ? (
        <div className="p-4">
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : (
        <ul className="grid gap-x-6 px-4 py-2 md:grid-cols-2">
          {(q.data?.items ?? []).map((s) => (
            <li
              key={s.key}
              className="flex items-center gap-3 border-hairline border-b py-2.5 last:border-b-0 md:[&:nth-last-child(2):nth-child(odd)]:border-b-0"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium text-ink text-ui">{s.label}</span>
                <span className="truncate text-caption text-ink-3" title={s.note}>
                  {s.note}
                </span>
              </span>
              <span
                className={cx(
                  "inline-flex h-6 shrink-0 items-center rounded-pill px-2.5 font-medium text-caption",
                  s.set ? "bg-success-soft text-success" : "bg-fill-2 text-ink-2",
                )}
              >
                {s.set ? "Set" : "Missing"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// Drawers

function Loading() {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-12 text-ink-3">
      <Spinner />
      <span className="text-caption">Loading</span>
    </div>
  );
}

function ProviderSheet({ name, onClose }: { name: SheetProvider; onClose: () => void }) {
  const q = useQuery(providerQuery(name));
  const draft = useProviderSettings(name, q.data);
  const test = useProviderTest(name);
  useUnsavedChanges(draft.dirty);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const b = BRAND[name];
  const hasSettings = PROVIDER_FIELDS[name].settings.length > 0;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await draft.save();
      test.clear();
      toast(`${b.title} saved.`);
    } catch (e) {
      setSaveError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Sheet
        open
        onOpenChange={(o) => {
          if (o) return;
          if (draft.dirty) setConfirm(true);
          else onClose();
        }}
        title={b.title}
        titleAside={<TitleAside name={name} />}
        media={<LogoTile name={name} size={40} />}
        status={q.data ? <StatusPill status={providerStatus(q.data)} /> : null}
        width="lg"
        footerStart={
          saveError ? (
            <span role="alert" className="text-caption text-danger-ink">
              {saveError}
            </span>
          ) : (
            <ChangeMeta change={q.data?.changed} />
          )
        }
        footer={
          q.data ? (
            <>
              <SlowWait active={test.running} label="Testing" state="connecting" />
              <Button
                onClick={test.run}
                disabled={test.running || draft.dirty}
                busy={test.running}
                {...(draft.dirty ? { title: "Save first" } : {})}
              >
                Test
              </Button>
              {hasSettings ? (
                <Button variant="primary" disabled={!draft.dirty} busy={saving} onClick={() => void save()}>
                  Save
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <ProviderBody
            name={name}
            config={q.data}
            draft={draft}
            onKeyChanged={test.clear}
            result={test.result}
          />
        )}
      </Sheet>
      <DiscardDialog
        open={confirm}
        title={b.title}
        onKeep={() => setConfirm(false)}
        onDiscard={() => {
          setConfirm(false);
          draft.reset();
          onClose();
        }}
      />
    </>
  );
}

function ProviderBody({
  name,
  config,
  draft,
  onKeyChanged,
  result,
}: {
  name: SheetProvider;
  config: ProviderConfig;
  draft: ProviderSettingsDraft;
  onKeyChanged: () => void;
  result: ProviderTestResult | null;
}) {
  const hasSettings = PROVIDER_FIELDS[name].settings.length > 0;
  const keysNeeded = keysStatus(name, config) === "Not set";
  const sections = useDrawerSections(`provider-${name}`, ["keys", "settings"], keysNeeded ? "keys" : null);
  return (
    <DrawerBody>
      <LastTest config={config} result={result} />
      {hasSettings ? (
        <div>
          <DrawerSection id="keys" title="Keys" status={keysStatus(name, config)} sections={sections}>
            <ProviderKeys name={name} config={config} onChanged={onKeyChanged} />
          </DrawerSection>
          <DrawerSection
            id="settings"
            title="Settings"
            status={settingsStatus(name, draft.values)}
            sections={sections}
          >
            <ProviderSettingsFields name={name} draft={draft} />
          </DrawerSection>
        </div>
      ) : (
        <DrawerGroup title="Keys">
          <ProviderKeys name={name} config={config} onChanged={onKeyChanged} />
        </DrawerGroup>
      )}
      <ProviderDanger name={name} config={config} onChanged={onKeyChanged} />
    </DrawerBody>
  );
}

function MicrosoftDrawer({
  onClose,
  attention,
  sender,
  senderConfig,
}: {
  onClose: () => void;
  attention: string | null;
  /** The company owns the Microsoft sender: the drawer mounts its editor. */
  sender: boolean;
  /** The sender's row in the provider list, for the pill until the drawer's own read lands. */
  senderConfig: ProviderConfig | null;
}) {
  const own = useQuery({ ...providerQuery("email"), enabled: sender });
  const email = useQuery(emailAdminQuery);
  const config = sender ? (own.data ?? senderConfig) : null;
  const st = email.data ? microsoftState(config, email.data) : null;
  return (
    <MicrosoftSheet
      onClose={onClose}
      attention={attention}
      sender={sender}
      media={<LogoTile name="microsoft" size={40} />}
      status={st ? <StatusPill status={st.status} label={st.label} /> : null}
    />
  );
}

function OdooSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery(integrationsQuery);
  const v = q.data?.find((x) => x.kind === "odoo");
  const put = (next: IntegrationView) => {
    qc.setQueryData<IntegrationView[]>(integrationsKey, (list) =>
      (list ?? []).map((x) => (x.kind === next.kind ? next : x)),
    );
    qc.invalidateQueries({ queryKey: qk.overview });
  };
  return (
    <OdooDrawer
      view={v ?? null}
      error={q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      onClose={onClose}
      media={<LogoTile name="odoo" size={40} />}
      status={v ? <StatusPill status={odooStatus(v)} /> : null}
      onSave={async (s) => {
        put(await integrationsApi.save("odoo", s));
        toast("Odoo settings saved.");
      }}
      onTest={async (s) => {
        const r = await integrationsApi.test("odoo", s);
        qc.invalidateQueries({ queryKey: integrationsKey });
        return r;
      }}
      onRemove={async () => {
        put(await integrationsApi.remove("odoo"));
        toast("Odoo setup removed.");
      }}
    />
  );
}
